import wrapperHOC from "@Utils/wrapperHOC";
import { memo, useCallback, useEffect } from "react";
import { useSelector, useDispatch, shallowEqual } from "react-redux";
import type { AppState } from "@Store/index";
import styles from "./BlogHome.module.scss";
import HeaderDesktop from "@Components/Header/HeaderDesktop";
import FooterDesktop from "@Components/FooterV2/FooterDesktop";
import Header from "@Components/Header";
import FooterV2 from "@Components/FooterV2/FooterV2";
import useIsDesktop from "hooks/useIsDesktop";
import BlogHeroSection from "./components/BlogHeroSection";
import CategoryTabs from "@Components/CategoryTabs";
import ContentGridSection from "@Components/ContentGridSection";
import BlogsSidebar from "./components/BlogsSidebar";
import getBlogsByCategory from "./data/getBlogsByCategory";
import { setActiveCategory } from "./data/blogHomeReducer";
import useTranslationHook from "hooks/useTranslationHook";
import AppPromotionBanner from "@Components/AppPromotionBanner";
import { PAGE } from "constants/configConstants";
import { updateShowAppBanner } from "@App/appReducer";
import useBlogBasePath from "@Pages/Blog/utils/useBlogBasePath";
import { getBlogLanguageFromPath } from "@Pages/Blog/utils/blogLanguage";

const BlogHome = () => {
  const dispatch = useDispatch();
  const isDesktop = useIsDesktop();
  const basePath = useBlogBasePath();

  const lang = getBlogLanguageFromPath(basePath);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  const {
    homeFeaturedBlogs,
    latestBlogs,
    categories,
    activeCategory,
    activeCategorySlug,
    categoryBlogs,
    categoryBlogsLoading,
    readyStatus,
  } = useSelector((state: AppState) => state.blogHomeData);

  const { showAppBanner } = useSelector(
    ({ appData }: AppState) => ({
      showAppBanner: appData.showAppBanner,
    }),
    shallowEqual,
  );

  const handleCloseBanner = () => {
    dispatch(updateShowAppBanner(false));
  };

  const { t } = useTranslationHook();
  const handleCategoryChange = useCallback(
    (category: string) => {
      // If "Latest" is selected, no need to fetch - we already have the data
      if (category.toLowerCase() === "latest") {
        dispatch(setActiveCategory({ name: "Latest", slug: "" }));
        return;
      }

      // Find the category object to get its slug
      const categoryObj = categories.find((cat) => cat.name === category);
      if (categoryObj) {
        dispatch(
          getBlogsByCategory({
            categorySlug: categoryObj.slug,
            categoryName: categoryObj.name,
            lang,
          }),
        );
      }
    },
    [dispatch, categories, lang],
  );

  const isGridLoading = categoryBlogsLoading || readyStatus === "request";
  const displayBlogs = activeCategory.toLowerCase() === "latest" ? latestBlogs : categoryBlogs;

  return (
    <>
      {isDesktop ? (
        <HeaderDesktop isBlogHeader parentPage={PAGE.BLOG} />
      ) : (
        <>
          {showAppBanner && (
            <AppPromotionBanner from={PAGE.BLOG} onClose={handleCloseBanner} addSticky />
          )}
          <Header isBlogHeader />
        </>
      )}
      <div className={styles.blogHomeContainer}>
        <div className={styles.contentWrapper}>
          {/* Main Content Column */}
          <div className={styles.mainColumn}>
            {/* Hero Section with Carousel */}
            <BlogHeroSection blogs={homeFeaturedBlogs} />
            {/* Horizontal Divider */}
            <div className={styles.horizontalDivider} />

            {/* Section Title */}
            <div className={styles.homeGridSection}>
              <div className={styles.sectionHeader}>
                <h2 className={styles.sectionTitle}>{t("blog.blogHome.title")}</h2>
                <p className={styles.sectionSubtitle}>{t("blog.blogHome.subtitle")}</p>
              </div>
              {/* Category Tabs */}
              <CategoryTabs
                categories={categories}
                activeCategory={activeCategory}
                onCategoryChange={handleCategoryChange}
              />
              {/* Blog Grid */}
              <ContentGridSection
                items={displayBlogs}
                basePath={basePath}
                contentLabel="blogs"
                isLoading={isGridLoading}
                activeCategory={activeCategory}
                activeCategorySlug={activeCategorySlug}
              />
            </div>
          </div>

          {/* Vertical Divider */}
          <div className={styles.verticalDivider} />

          {/* Sidebar Column */}
          <BlogsSidebar />
        </div>
      </div>
      {isDesktop ? <FooterDesktop /> : <FooterV2 />}
    </>
  );
};

export default wrapperHOC(memo(BlogHome), {
  componentName: "BlogHome-Blog",
  showForChina: true,
});
